import React, { useState, useEffect, useRef, lazy, Suspense, Component } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import Layout from './components/layout/Layout';
import { ToastProvider } from './components/common/ToastProvider';
import LoginModal from './components/common/LoginModal';
import LoadingScreen from './components/common/LoadingScreen';
import WelcomeModal from './components/common/WelcomeModal';
import { supabase } from './config';
import { apiRequest } from './utils/api';
import { getAuthRedirectOrigin } from './utils/authRedirect';

const Profile = lazy(() => import('./components/profile/Profile'));
const Admin = lazy(() => import('./components/admin/Admin'));
const About = lazy(() => import('./components/pages/About'));
const HomePage = lazy(() => import('./components/pages/HomePage'));
const CreateActivity = lazy(() => import('./components/pages/CreateActivity'));
const MyBookings = lazy(() => import('./components/pages/MyBookings'));
const EventDetail = lazy(() => import('./components/views/EventDetail'));
const WalkDetail = lazy(() => import('./components/views/WalkDetail'));
const EventsPage = lazy(() => import('./components/events/EventsPage'));
const Privacy = lazy(() => import('./components/pages/Privacy'));
const Terms = lazy(() => import('./components/pages/Terms'));
const DataDeletion = lazy(() => import('./components/pages/DataDeletion'));
const ResourcesPage = lazy(() => import('./components/pages/ResourcesPage'));
const CommunityPage = lazy(() => import('./components/pages/CommunityPage'));
const CommunityIntroductions = lazy(() => import('./components/pages/CommunityIntroductions'));
const CommunityBusinesses = lazy(() => import('./components/pages/CommunityBusinesses'));
const SupportPage = lazy(() => import('./components/pages/SupportPage'));
const ResetPassword = lazy(() => import('./components/pages/ResetPassword'));
const InboxPage = lazy(() => import('./components/pages/InboxPage'));
const PublicProfile = lazy(() => import('./components/pages/PublicProfile'));
const NewsletterUnsubscribe = lazy(() => import('./components/pages/NewsletterUnsubscribe'));
const MenuPage = lazy(() => import('./components/pages/MenuPage'));

const LoadingFallback = () => <LoadingScreen />;

// Error Boundary
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('App error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-surface p-6">
          <div className="text-center max-w-md">
            <span className="material-symbols-outlined text-5xl text-stone-300 mb-4 block">error_outline</span>
            <h1 className="text-2xl font-headline text-stone-800 mb-2">Something went wrong</h1>
            <p className="text-stone-500 mb-6">We hit an unexpected error. Please try refreshing the page.</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-primary text-white px-6 py-2.5 rounded-full font-semibold"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// 404 Page
function NotFound() {
  return (
    <div className="flex items-center justify-center py-20 px-6">
      <div className="text-center max-w-md">
        <span className="material-symbols-outlined text-6xl text-stone-200 mb-4 block">explore_off</span>
        <h1 className="text-3xl font-headline text-stone-800 mb-2">Page not found</h1>
        <p className="text-stone-500 mb-6">The page you're looking for doesn't exist or has been moved.</p>
        <Link to="/" className="bg-primary text-white px-6 py-2.5 rounded-full font-semibold inline-block">
          Go Home
        </Link>
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginModalMode, setLoginModalMode] = useState('signin');
  const [profileNameRequired, setProfileNameRequired] = useState(false);
  const [nameForm, setNameForm] = useState({ firstName: '', lastName: '' });
  const [savingName, setSavingName] = useState(false);
  const nameModalRef = useRef(null);
  const firstNameInputRef = useRef(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setUser(session.user);
        fetchUserProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setUser(session.user);
        fetchUserProfile(session.user.id);
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const handleOpenLoginModal = (event) => {
      const nextMode = event?.detail?.mode;
      if (nextMode === 'signup' || nextMode === 'signin' || nextMode === 'forgot-password') {
        setLoginModalMode(nextMode);
      } else {
        setLoginModalMode('signin');
      }
      setShowLoginModal(true);
    };
    window.addEventListener('openLoginModal', handleOpenLoginModal);
    return () => window.removeEventListener('openLoginModal', handleOpenLoginModal);
  }, []);

  useEffect(() => {
    if (!profileNameRequired) {
      return;
    }

    const focusTimer = window.setTimeout(() => {
      firstNameInputRef.current?.focus();
    }, 0);

    const handleKeyDown = (event) => {
      if (event.key !== 'Tab' || !nameModalRef.current) {
        return;
      }

      const focusableElements = nameModalRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const focusable = Array.from(focusableElements);

      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const firstFocusable = focusable[0];
      const lastFocusable = focusable[focusable.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      } else if (!event.shiftKey && activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [profileNameRequired]);

  const fetchUserProfile = async (userId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setLoading(false);
        return;
      }
      const userData = await apiRequest(`/auth-profile/${userId}`, {
        token: session.access_token,
      });
      const mergedUser = { ...session.user, ...userData, id: userId };
      setUser(mergedUser);

      const hasFirstName = Boolean(String(mergedUser.first_name || '').trim());
      const hasLastName = Boolean(String(mergedUser.last_name || '').trim());
      if (!hasFirstName || !hasLastName) {
        setProfileNameRequired(true);
        setNameForm({
          firstName: String(mergedUser.first_name || '').trim(),
          lastName: String(mergedUser.last_name || '').trim(),
        });
      } else {
        setProfileNameRequired(false);
      }
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteName = async (e) => {
    e.preventDefault();
    const firstName = nameForm.firstName.trim();
    const lastName = nameForm.lastName.trim();

    if (!firstName || !lastName) {
      return;
    }

    setSavingName(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setSavingName(false);
        return;
      }

      const updated = await apiRequest('/users-me', {
        method: 'PUT',
        token: session.access_token,
        body: {
          first_name: firstName,
          last_name: lastName,
          suburb: user?.suburb || null,
          children: Array.isArray(user?.children) ? user.children : undefined,
          baby_name: user?.baby_name || null,
          baby_due_date: user?.baby_due_date || null,
          baby_birth_date: user?.baby_birth_date || null,
          interests: user?.interests || [],
        },
      });

      setUser((prev) => ({
        ...prev,
        ...updated,
        first_name: firstName,
        last_name: lastName,
        name: `${firstName} ${lastName}`.trim(),
      }));
      setProfileNameRequired(false);
    } catch (err) {
      console.error('Failed to complete profile name:', err);
    } finally {
      setSavingName(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  const openLoginModal = (mode = 'signin') => {
    setLoginModalMode(mode);
    setShowLoginModal(true);
  };
  const closeLoginModal = () => {
    setShowLoginModal(false);
    setLoginModalMode('signin');
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <ErrorBoundary>
    <ToastProvider>
      {profileNameRequired && user && (
        <div className="fixed inset-0 z-confirm bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div
            className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="profile-name-required-title"
            ref={nameModalRef}
          >
            <h2 id="profile-name-required-title" className="text-2xl font-headline text-primary mb-2">Complete Your Profile</h2>
            <p className="text-stone-600 text-sm mb-5">Please add your first and last name to continue.</p>
            <form onSubmit={handleCompleteName} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={nameForm.firstName}
                  onChange={(e) => setNameForm((prev) => ({ ...prev, firstName: e.target.value }))}
                  required
                  maxLength={80}
                  autoComplete="given-name"
                  ref={firstNameInputRef}
                  className="w-full px-4 py-3 border-2 border-stone-200 rounded-xl focus:border-primary focus:ring-0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={nameForm.lastName}
                  onChange={(e) => setNameForm((prev) => ({ ...prev, lastName: e.target.value }))}
                  required
                  maxLength={80}
                  autoComplete="family-name"
                  className="w-full px-4 py-3 border-2 border-stone-200 rounded-xl focus:border-primary focus:ring-0"
                />
              </div>
              <button
                type="submit"
                disabled={savingName}
                className="w-full py-4 rounded-full bg-gradient-to-br from-primary to-primary-dim text-white font-bold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {savingName ? 'Saving...' : 'Save and Continue'}
              </button>
            </form>
          </div>
        </div>
      )}
      <LoginModal 
        isOpen={showLoginModal} 
        initialMode={loginModalMode}
        onClose={closeLoginModal}
        onLoginSuccess={() => {}}
      />
      <WelcomeModal user={user} />
      <Router>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route 
              path="/" 
              element={
                <Layout 
                  user={user} 
                  onLogin={openLoginModal} 
                  onLogout={handleLogout} 
                />
              }
            >
              <Route index element={<HomePage user={user} />} />
              <Route path="about" element={<About user={user} />} />
              <Route 
                path="create-activity" 
                element={user ? <CreateActivity user={user} /> : <Navigate to="/" />} 
              />
              <Route 
                path="profile" 
                element={user ? <Profile user={user} onLogout={handleLogout} /> : <Navigate to="/" />} 
              />
              <Route
                path="mums/:id"
                element={user ? <PublicProfile user={user} /> : <Navigate to="/" />}
              />
              <Route 
                path="my-bookings" 
                element={user ? <MyBookings user={user} /> : <Navigate to="/" />} 
              />
              <Route 
                path="inbox" 
                element={user ? <InboxPage user={user} /> : <Navigate to="/" />} 
              />
              <Route
                path="activities"
                element={user ? <EventsPage user={user} /> : <Navigate to="/" />}
              />
              <Route 
                path="community" 
                element={user ? <CommunityPage user={user} /> : <Navigate to="/" />} 
              />
              <Route
                path="community/introductions"
                element={user ? <CommunityIntroductions user={user} /> : <Navigate to="/" />}
              />
              <Route
                path="community/businesses"
                element={<CommunityBusinesses user={user} />}
              />
              <Route
                path="community/discussions"
                element={<Navigate to="/" replace />}
              />
              <Route
                path="community/chatrooms"
                element={<Navigate to="/" replace />}
              />
              <Route 
                path="support" 
                element={<SupportPage user={user} />} 
              />
              <Route 
                path="reset-password" 
                element={<ResetPassword />} 
              />
              <Route 
                path="resources" 
                element={<ResourcesPage />} 
              />
              <Route
                path="menu"
                element={<MenuPage user={user} onLogin={openLoginModal} onLogout={handleLogout} />}
              />
              <Route 
                path="admin" 
                element={user?.is_admin ? <Admin user={user} /> : <Navigate to="/" />} 
              />
              <Route
                path="event/:id"
                element={user ? <EventDetail user={user} supabase={supabase} /> : <Navigate to="/" />}
              />
              <Route
                path="walk/:id"
                element={user ? <WalkDetail user={user} supabase={supabase} /> : <Navigate to="/" />}
              />
              <Route path="privacy" element={<Privacy />} />
              <Route path="terms" element={<Terms />} />
              <Route path="data-deletion" element={<DataDeletion />} />
              <Route path="unsubscribe" element={<NewsletterUnsubscribe />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
      </Router>
    </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
